import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useIsPro } from "@/hooks/useIsPro";
import { syncSentryUser, syncSentryTier } from "@/lib/sentryUser";

/**
 * Issue #642 — mirrors the signed-in Supabase user id onto Sentry's scope so
 * issues report a real "Users" count instead of 0. Renders nothing.
 *
 * ── Why this is a component and not two lines in useAuth.tsx ────────────────
 * `useAuth.tsx` is a locked zone (CLAUDE.md) and the issue is explicit: add
 * the Sentry calls, do not alter the auth flow. Reading the context the
 * AuthProvider already publishes keeps the diff strictly additive — this file
 * makes zero auth calls of its own. In particular it does NOT register its own
 * `supabase.auth.onAuthStateChange` subscriber: that call path resolves the
 * initial session through supabase-js's exclusive Web Lock, which is the
 * mechanism behind the June 29, 2026 `AbortError: Lock broken by another
 * request with the 'steal' option` outage. A no-cost feature must not put
 * another acquirer on that lock.
 *
 * ── Why it waits for `loading` ──────────────────────────────────────────────
 * At mount `user` is null only because `getSession()` hasn't resolved yet, not
 * because nobody is signed in. Clearing on that first pass is harmless today
 * but reads as "sign-out" and would race a re-mount; gating on `loading`
 * means the first value Sentry sees is the resolved one.
 *
 * ── Why there is no unmount cleanup ─────────────────────────────────────────
 * `AuthProvider` sits inside `OnlineGate` (App.tsx), so losing the network
 * unmounts this whole subtree. Clearing the Sentry user there would strip the
 * identity off exactly the errors most worth attributing — the offline ones.
 * Sign-out already flows through `user -> null` while still mounted, which is
 * the case that genuinely needs clearing.
 *
 * ── Why the tier tag waits on `proLoaded` ───────────────────────────────────
 * #870 also wants a free/pro tag, read through `useIsPro` (#631) rather than a
 * second entitlement check. `useIsPro()` returns `proLoaded && (...)`, so
 * before the entitlement row resolves it is indistinguishable from a real
 * "free" — tagging on that first pass would file every Pro mom's early errors
 * under free. `proLoaded` comes off the same context, so this adds no query.
 * The two effects are separate on purpose: the identity should not wait on
 * entitlements to reach Sentry.
 *
 * ── Why there is no third effect for the `internal` tag ─────────────────────
 * #1476's `internal:true|false` tag is a pure function of the same user id, so
 * `syncSentryUser` writes it itself. A third effect here would have identical
 * deps to the first and could bail on a path the first did not, producing the
 * one state the tag exists to prevent: an internal uuid on an untagged event.
 * Unlike `tier` it must NOT wait on `proLoaded` — membership needs no query.
 */
const SentryUserSync = () => {
  const { user, loading, proLoaded } = useAuth();
  const isPro = useIsPro();
  const userId = user?.id;

  useEffect(() => {
    if (loading) return;
    syncSentryUser(userId);
  }, [userId, loading]);

  useEffect(() => {
    if (loading) return;
    // Signed out: clear the tag outright. Signed in but entitlements still in
    // flight: leave whatever is there rather than guess.
    if (!userId) {
      syncSentryTier(null);
      return;
    }
    if (!proLoaded) return;
    syncSentryTier(isPro ? "pro" : "free");
  }, [userId, isPro, proLoaded, loading]);

  return null;
};

export default SentryUserSync;
